import { useEffect, useRef } from 'react';
import * as THREE from 'three';
import './LiquidEther.css';

export default function LiquidEther({
  mouseForce = 20, cursorSize = 100, isViscous = false, viscous = 30,
  iterationsViscous = 32, iterationsPoisson = 32, dt = 0.014, BFECC = true,
  resolution = 0.5, isBounce = false,
  colors = ['#5227FF', '#FF9FFC', '#B19EEF'], style = {}, className = '',
  autoDemo = true, autoSpeed = 0.5, autoIntensity = 2.2,
  takeoverDuration = 0.25, autoResumeDelay = 3000, autoRampDuration = 0.6
}) {
  const mountRef = useRef(null);

  useEffect(() => {
    const container = mountRef.current;
    if (!container) return;

    // ── helpers ──────────────────────────────────────────────
    function makePaletteTexture(stops) {
      const arr = stops.length < 2 ? [stops[0]||'#fff', stops[0]||'#fff'] : stops;
      const data = new Uint8Array(arr.length * 4);
      arr.forEach((s, i) => {
        const c = new THREE.Color(s);
        data[i*4]=Math.round(c.r*255); data[i*4+1]=Math.round(c.g*255);
        data[i*4+2]=Math.round(c.b*255); data[i*4+3]=255;
      });
      const t = new THREE.DataTexture(data, arr.length, 1, THREE.RGBAFormat);
      t.magFilter = t.minFilter = THREE.LinearFilter;
      t.wrapS = t.wrapT = THREE.ClampToEdgeWrapping;
      t.generateMipmaps = false; t.needsUpdate = true;
      return t;
    }

    // ── renderer ─────────────────────────────────────────────
    const W = container.offsetWidth  || container.clientWidth  || 800;
    const H = container.offsetHeight || container.clientHeight || 600;
    const PR = Math.min(window.devicePixelRatio || 1, 2);

    const renderer = new THREE.WebGLRenderer({ antialias: false, alpha: true });
    renderer.autoClear = false;
    renderer.setClearColor(0x000000, 0);
    renderer.setPixelRatio(PR);
    renderer.setSize(W, H);

    const canvas = renderer.domElement;
    canvas.style.cssText = 'position:absolute;top:0;left:0;width:100%;height:100%;display:block;pointer-events:none;';
    container.appendChild(canvas);

    // ── shaders ───────────────────────────────────────────────
    const face_vert=`attribute vec3 position;uniform vec2 px;uniform vec2 boundarySpace;varying vec2 uv;precision highp float;void main(){vec3 pos=position;vec2 scale=1.0-boundarySpace*2.0;pos.xy=pos.xy*scale;uv=vec2(0.5)+(pos.xy)*0.5;gl_Position=vec4(pos,1.0);}`;
    const line_vert=`attribute vec3 position;uniform vec2 px;precision highp float;varying vec2 uv;void main(){vec3 pos=position;uv=0.5+pos.xy*0.5;vec2 n=sign(pos.xy);pos.xy=abs(pos.xy)-px*1.0;pos.xy*=n;gl_Position=vec4(pos,1.0);}`;
    const mouse_vert=`precision highp float;attribute vec3 position;attribute vec2 uv;uniform vec2 center;uniform vec2 scale;uniform vec2 px;varying vec2 vUv;void main(){vec2 pos=position.xy*scale*2.0*px+center;vUv=uv;gl_Position=vec4(pos,0.0,1.0);}`;
    const advection_frag=`precision highp float;uniform sampler2D velocity;uniform float dt;uniform bool isBFECC;uniform vec2 fboSize;uniform vec2 px;varying vec2 uv;void main(){vec2 ratio=max(fboSize.x,fboSize.y)/fboSize;if(isBFECC==false){vec2 vel=texture2D(velocity,uv).xy;vec2 uv2=uv-vel*dt*ratio;vec2 newVel=texture2D(velocity,uv2).xy;gl_FragColor=vec4(newVel,0.0,0.0);}else{vec2 spot_new=uv;vec2 vel_old=texture2D(velocity,uv).xy;vec2 spot_old=spot_new-vel_old*dt*ratio;vec2 vel_new1=texture2D(velocity,spot_old).xy;vec2 spot_new2=spot_old+vel_new1*dt*ratio;vec2 error=spot_new2-spot_new;vec2 spot_new3=spot_new-error/2.0;vec2 vel_2=texture2D(velocity,spot_new3).xy;vec2 spot_old2=spot_new3-vel_2*dt*ratio;vec2 newVel2=texture2D(velocity,spot_old2).xy;gl_FragColor=vec4(newVel2,0.0,0.0);}}`;
    const color_frag=`precision highp float;uniform sampler2D velocity;uniform sampler2D palette;uniform vec4 bgColor;varying vec2 uv;void main(){vec2 vel=texture2D(velocity,uv).xy;float lenv=clamp(length(vel)*3.0,0.0,1.0);vec3 c=texture2D(palette,vec2(lenv,0.5)).rgb;vec3 outRGB=mix(bgColor.rgb,c,lenv);float outA=mix(bgColor.a,1.0,lenv);gl_FragColor=vec4(outRGB,outA);}`;
    const divergence_frag=`precision highp float;uniform sampler2D velocity;uniform float dt;uniform vec2 px;varying vec2 uv;void main(){float x0=texture2D(velocity,uv-vec2(px.x,0.0)).x;float x1=texture2D(velocity,uv+vec2(px.x,0.0)).x;float y0=texture2D(velocity,uv-vec2(0.0,px.y)).y;float y1=texture2D(velocity,uv+vec2(0.0,px.y)).y;float divergence=(x1-x0+y1-y0)/2.0;gl_FragColor=vec4(divergence/dt);}`;
    const externalForce_frag=`precision highp float;uniform vec2 force;uniform vec2 center;uniform vec2 scale;uniform vec2 px;varying vec2 vUv;void main(){vec2 circle=(vUv-0.5)*2.0;float d=1.0-min(length(circle),1.0);d*=d;gl_FragColor=vec4(force*d,0.0,1.0);}`;
    const poisson_frag=`precision highp float;uniform sampler2D pressure;uniform sampler2D divergence;uniform vec2 px;varying vec2 uv;void main(){float p0=texture2D(pressure,uv+vec2(px.x*2.0,0.0)).r;float p1=texture2D(pressure,uv-vec2(px.x*2.0,0.0)).r;float p2=texture2D(pressure,uv+vec2(0.0,px.y*2.0)).r;float p3=texture2D(pressure,uv-vec2(0.0,px.y*2.0)).r;float div=texture2D(divergence,uv).r;float newP=(p0+p1+p2+p3)/4.0-div;gl_FragColor=vec4(newP);}`;
    const pressure_frag=`precision highp float;uniform sampler2D pressure;uniform sampler2D velocity;uniform vec2 px;uniform float dt;varying vec2 uv;void main(){float step=1.0;float p0=texture2D(pressure,uv+vec2(px.x*step,0.0)).r;float p1=texture2D(pressure,uv-vec2(px.x*step,0.0)).r;float p2=texture2D(pressure,uv+vec2(0.0,px.y*step)).r;float p3=texture2D(pressure,uv-vec2(0.0,px.y*step)).r;vec2 v=texture2D(velocity,uv).xy;vec2 gradP=vec2(p0-p1,p2-p3)*0.5;v=v-gradP*dt;gl_FragColor=vec4(v,0.0,1.0);}`;
    const viscous_frag=`precision highp float;uniform sampler2D velocity;uniform sampler2D velocity_new;uniform float v;uniform vec2 px;uniform float dt;varying vec2 uv;void main(){vec2 old=texture2D(velocity,uv).xy;vec2 new0=texture2D(velocity_new,uv+vec2(px.x*2.0,0.0)).xy;vec2 new1=texture2D(velocity_new,uv-vec2(px.x*2.0,0.0)).xy;vec2 new2=texture2D(velocity_new,uv+vec2(0.0,px.y*2.0)).xy;vec2 new3=texture2D(velocity_new,uv-vec2(0.0,px.y*2.0)).xy;vec2 newv=4.0*old+v*dt*(new0+new1+new2+new3);newv/=4.0*(1.0+v*dt);gl_FragColor=vec4(newv,0.0,0.0);}`;

    // ── FBO ──────────────────────────────────────────────────
    const isIOS = /(iPad|iPhone|iPod)/i.test(navigator.userAgent);
    const fboType = isIOS ? THREE.HalfFloatType : THREE.FloatType;
    const fboOpts = { type:fboType, depthBuffer:false, stencilBuffer:false, minFilter:THREE.LinearFilter, magFilter:THREE.LinearFilter, wrapS:THREE.ClampToEdgeWrapping, wrapT:THREE.ClampToEdgeWrapping };

    function makeFBO(w, h) { return new THREE.WebGLRenderTarget(w, h, fboOpts); }

    let simW = Math.max(1, Math.round(resolution * W));
    let simH = Math.max(1, Math.round(resolution * H));
    let cellScale = new THREE.Vector2(1/simW, 1/simH);
    let fboSize   = new THREE.Vector2(simW, simH);
    let boundarySpace = isBounce ? new THREE.Vector2(0,0) : cellScale.clone();

    const fbos = {
      vel_0: makeFBO(simW,simH), vel_1: makeFBO(simW,simH),
      vel_v0: makeFBO(simW,simH), vel_v1: makeFBO(simW,simH),
      div: makeFBO(simW,simH),
      p0: makeFBO(simW,simH), p1: makeFBO(simW,simH),
    };

    // ── scene helpers ────────────────────────────────────────
    function makePass(vertSrc, fragSrc, uniforms, output) {
      const scene    = new THREE.Scene();
      const camera   = new THREE.Camera();
      const mat      = new THREE.RawShaderMaterial({ vertexShader:vertSrc, fragmentShader:fragSrc, uniforms });
      const mesh     = new THREE.Mesh(new THREE.PlaneGeometry(2,2), mat);
      scene.add(mesh);
      return {
        scene, camera, mat, mesh,
        render(target) {
          renderer.setRenderTarget(target||null);
          renderer.render(scene, camera);
          renderer.setRenderTarget(null);
        }
      };
    }

    const paletteTex = makePaletteTexture(colors);
    const bgVec4     = new THREE.Vector4(0,0,0,0);

    // advection
    const advPass = makePass(face_vert, advection_frag, {
      boundarySpace:{value:cellScale}, px:{value:cellScale}, fboSize:{value:fboSize},
      velocity:{value:fbos.vel_0.texture}, dt:{value:dt}, isBFECC:{value:BFECC}
    });
    // boundary line for advection
    {
      const g = new THREE.BufferGeometry();
      const v = new Float32Array([-1,-1,0,-1,1,0,-1,1,0,1,1,0,1,1,0,1,-1,0,1,-1,0,-1,-1,0]);
      g.setAttribute('position', new THREE.BufferAttribute(v,3));
      const line = new THREE.LineSegments(g, new THREE.RawShaderMaterial({ vertexShader:line_vert, fragmentShader:advection_frag, uniforms:advPass.mat.uniforms }));
      advPass.scene.add(line);
      advPass.line = line;
    }

    // external force
    const forcePass = (() => {
      const scene  = new THREE.Scene();
      const camera = new THREE.Camera();
      const mat = new THREE.RawShaderMaterial({
        vertexShader: mouse_vert, fragmentShader: externalForce_frag,
        blending: THREE.AdditiveBlending, depthWrite: false,
        uniforms: { px:{value:cellScale}, force:{value:new THREE.Vector2()}, center:{value:new THREE.Vector2()}, scale:{value:new THREE.Vector2(cursorSize,cursorSize)} }
      });
      scene.add(new THREE.Mesh(new THREE.PlaneGeometry(1,1), mat));
      return { scene, camera, mat, render(target){renderer.setRenderTarget(target||null);renderer.render(scene,camera);renderer.setRenderTarget(null);} };
    })();

    // viscous
    const viscPass = makePass(face_vert, viscous_frag, {
      boundarySpace:{value:boundarySpace}, velocity:{value:fbos.vel_1.texture},
      velocity_new:{value:fbos.vel_v0.texture}, v:{value:viscous}, px:{value:cellScale}, dt:{value:dt}
    });

    // divergence
    const divPass = makePass(face_vert, divergence_frag, {
      boundarySpace:{value:boundarySpace}, velocity:{value:fbos.vel_v0.texture}, px:{value:cellScale}, dt:{value:dt}
    });

    // poisson
    const poisPass = makePass(face_vert, poisson_frag, {
      boundarySpace:{value:boundarySpace}, pressure:{value:fbos.p0.texture}, divergence:{value:fbos.div.texture}, px:{value:cellScale}
    });

    // pressure subtract
    const presPass = makePass(face_vert, pressure_frag, {
      boundarySpace:{value:boundarySpace}, pressure:{value:fbos.p0.texture}, velocity:{value:fbos.vel_v0.texture}, px:{value:cellScale}, dt:{value:dt}
    });

    // color output
    const colorScene  = new THREE.Scene();
    const colorCamera = new THREE.Camera();
    const colorMat    = new THREE.RawShaderMaterial({
      vertexShader: face_vert, fragmentShader: color_frag, transparent: true, depthWrite: false,
      uniforms: { boundarySpace:{value:new THREE.Vector2()}, velocity:{value:fbos.vel_0.texture}, palette:{value:paletteTex}, bgColor:{value:bgVec4} }
    });
    colorScene.add(new THREE.Mesh(new THREE.PlaneGeometry(2,2), colorMat));

    // ── mouse state ──────────────────────────────────────────
    let mCoords  = new THREE.Vector2(0,0);
    let mOld     = new THREE.Vector2(0,0);
    let mDiff    = new THREE.Vector2(0,0);
    let mMoved   = false;
    let mTimer   = null;
    let autoActive = false;
    let autoCurrent = new THREE.Vector2(0,0);
    let autoTarget  = new THREE.Vector2((Math.random()*2-1)*0.8, (Math.random()*2-1)*0.8);
    let autoLastT   = performance.now();
    let userLastT   = performance.now();

    function pickAutoTarget() {
      autoTarget.set((Math.random()*2-1)*0.8, (Math.random()*2-1)*0.8);
    }

    function onMouseMove(e) {
      const r = container.getBoundingClientRect();
      if (r.width===0||r.height===0) return;
      if (e.clientX<r.left||e.clientX>r.right||e.clientY<r.top||e.clientY>r.bottom) return;
      const nx = (e.clientX-r.left)/r.width*2-1;
      const ny = -((e.clientY-r.top)/r.height*2-1);
      mCoords.set(nx,ny); mMoved=true; userLastT=performance.now();
      autoActive=false;
      if(mTimer) clearTimeout(mTimer);
      mTimer = setTimeout(()=>{mMoved=false;},100);
    }
    function onTouchMove(e) {
      if(e.touches.length!==1) return;
      onMouseMove({clientX:e.touches[0].clientX, clientY:e.touches[0].clientY});
    }
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('touchmove', onTouchMove, {passive:true});

    // ── animation loop ────────────────────────────────────────
    let raf = null;
    let running = true;

    function simulate() {
      const now = performance.now();

      // auto-demo driver
      if (autoDemo && !autoActive && (now - userLastT) > autoResumeDelay) {
        autoActive = true; autoCurrent.copy(mCoords); autoLastT = now;
      }
      if (autoActive) {
        const elapsed = (now-autoLastT)/1000;
        const rampT = autoRampDuration>0 ? Math.min(1,elapsed/(autoRampDuration)) : 1;
        const ramp = rampT*rampT*(3-2*rampT);
        const dir = new THREE.Vector2().subVectors(autoTarget, autoCurrent);
        if (dir.length()<0.01) { pickAutoTarget(); }
        else {
          dir.normalize().multiplyScalar(autoSpeed*(now-autoLastT)/1000*ramp);
          autoCurrent.add(dir);
          mCoords.copy(autoCurrent);
          mMoved = true;
        }
        autoLastT = now;
      }

      mDiff.subVectors(mCoords, mOld);
      mOld.copy(mCoords);
      if (autoActive) mDiff.multiplyScalar(autoIntensity);

      // advection
      advPass.mat.uniforms.velocity.value = fbos.vel_0.texture;
      advPass.mat.uniforms.dt.value = dt;
      advPass.mat.uniforms.isBFECC.value = BFECC;
      if (advPass.line) advPass.line.visible = isBounce;
      advPass.render(fbos.vel_1);

      // external force
      if (mMoved) {
        const fx = mDiff.x * mouseForce * 0.5;
        const fy = mDiff.y * mouseForce * 0.5;
        const sx = cursorSize * cellScale.x;
        const sy = cursorSize * cellScale.y;
        const cx = Math.min(Math.max(mCoords.x, -1+sx), 1-sx);
        const cy = Math.min(Math.max(mCoords.y, -1+sy), 1-sy);
        forcePass.mat.uniforms.force.value.set(fx,fy);
        forcePass.mat.uniforms.center.value.set(cx,cy);
        forcePass.mat.uniforms.scale.value.set(cursorSize,cursorSize);
        forcePass.render(fbos.vel_1);
      }

      // viscous or pass-through
      let velSrc = fbos.vel_1;
      if (isViscous) {
        let fi = fbos.vel_v0, fo = fbos.vel_v1;
        viscPass.mat.uniforms.velocity.value = fbos.vel_1.texture;
        viscPass.mat.uniforms.v.value = viscous;
        viscPass.mat.uniforms.dt.value = dt;
        for (let i=0;i<iterationsViscous;i++) {
          viscPass.mat.uniforms.velocity_new.value = fi.texture;
          viscPass.render(fo);
          [fi,fo]=[fo,fi];
        }
        velSrc = fi;
      }

      // divergence
      divPass.mat.uniforms.velocity.value = velSrc.texture;
      divPass.render(fbos.div);

      // poisson
      let pi=fbos.p0, po=fbos.p1;
      poisPass.mat.uniforms.divergence.value = fbos.div.texture;
      for (let i=0;i<iterationsPoisson;i++) {
        poisPass.mat.uniforms.pressure.value = pi.texture;
        poisPass.render(po);
        [pi,po]=[po,pi];
      }

      // pressure subtract
      presPass.mat.uniforms.velocity.value = velSrc.texture;
      presPass.mat.uniforms.pressure.value = pi.texture;
      presPass.render(fbos.vel_0);

      // color output to screen
      colorMat.uniforms.velocity.value = fbos.vel_0.texture;
      renderer.setRenderTarget(null);
      renderer.render(colorScene, colorCamera);
    }

    function loop() {
      if (!running) return;
      simulate();
      raf = requestAnimationFrame(loop);
    }
    loop();

    // ── resize ───────────────────────────────────────────────
    function resize() {
      const nw = container.offsetWidth  || container.clientWidth  || W;
      const nh = container.offsetHeight || container.clientHeight || H;
      renderer.setSize(nw, nh);
      simW = Math.max(1, Math.round(resolution*nw));
      simH = Math.max(1, Math.round(resolution*nh));
      cellScale.set(1/simW,1/simH);
      fboSize.set(simW,simH);
      for (const k in fbos) fbos[k].setSize(simW,simH);
    }
    const ro = new ResizeObserver(()=>{ resize(); });
    ro.observe(container);
    window.addEventListener('resize', resize);

    return () => {
      running = false;
      if (raf) cancelAnimationFrame(raf);
      if (mTimer) clearTimeout(mTimer);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('resize', resize);
      try { ro.disconnect(); } catch(e) {}
      for (const k in fbos) fbos[k].dispose();
      renderer.dispose();
      if (canvas.parentNode) canvas.parentNode.removeChild(canvas);
    };
  }, []);

  return <div ref={mountRef} className={`liquid-ether-container${className?' '+className:''}`} style={style} />;
}
